import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  useLocation,
  useNavigate,
  useNavigationType,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ChevronLeft, CircleAlert, Clock } from 'lucide-react';

import { usePinnedPage } from '../../hooks/usePinnedPage';
import {
  FLOW_STEPS,
  STEP_FIELDS,
  flowFromParam,
  stepOf,
} from '../../utils/appWelcome';
import { trackEvent } from '../../utils/gt';
import { useKeyboardInset } from '../../utils/useKeyboardInset';
import { useResolvedTheme } from '../../utils/theme';

import { TurnstileField } from '../TurnstileField';
import {
  Field,
  ForgotForm,
  ForgotSent,
  PasswordInput,
  ServerAlert,
  SignupSent,
  SubmitButton,
} from './AuthForms';
import { emailRule } from './authRules';
import { useAuthRequests } from './useAuthRequests';
import '../../styles/pages/auth.css';
import '../../styles/pages/app-welcome.css';

const LOGO = '/icon-512.png';

/**
 * Signed out inside the installed app on a phone (owner decision 30/10/2026, option A): a full screen built on the
 * logo, with two doors, then one question per screen, a thin bar counting them. No header, tab bar, cookie card or
 * pop-up. /login/<page> (an expired session) opens straight on the login's first question and returns to that page.
 * The logo stays on screen and shrinks into the steps; the requests are the sign-in window's own (useAuthRequests).
 * The whole screen is locked: a fixed layer over a pinned page, which only moves up and down, inside itself, when the
 * keyboard leaves it short.
 */
export const AppWelcome = () => {
  usePinnedPage();
  const keyboard = useKeyboardInset();
  const { t } = useTranslation();
  const copy = useCallback(
    (key, options) => t(`components.AuthDialog.${key}`, options),
    [t]
  );
  const own = (key, options) => t(`components.AppWelcome.${key}`, options);
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const { pathname, search, state, key: locationKey } = useLocation();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useResolvedTheme();

  const isLoginRoute = pathname.startsWith('/login');
  const fromSignupLink = searchParams.get('cadastro') === '1';
  const flow =
    flowFromParam(searchParams.get('flow')) ??
    (isLoginRoute ? 'login' : fromSignupLink ? 'signup' : null);
  const redirectUrl = params['*'];
  const isExpired = isLoginRoute && !state?.quiet;
  const afterSignupPath = fromSignupLink ? '/workout' : undefined;

  // What the page's address carried before the steps (an expired session's own ?w=…), for going back to it
  const pageSearch = () => {
    const kept = new URLSearchParams(search);
    kept.delete('flow');
    kept.delete('step');
    const text = kept.toString();
    return text ? `?${text}` : '';
  };

  const goStep = (nextFlow, step, options) => {
    const next = new URLSearchParams(search);
    next.set('flow', nextFlow);
    next.set('step', String(step));
    setSearchParams(next, options);
  };

  const requests = useAuthRequests({
    afterSignupPath,
    onLoggedIn: ({ fromSignupLink: firstLogin }) => {
      navigate(
        isLoginRoute
          ? `/${redirectUrl || ''}${pageSearch()}`
          : firstLogin
            ? '/workout'
            : '/',
        { replace: true }
      );
    },
    onSignedUp: () => {
      toast(t('notifications.signupSuccess'));
      navigate(afterSignupPath || '/?w=true', { replace: true });
    },
    onSignupSent: () => goStep('signupSent', 1, { replace: true }),
    onResetSent: () => goStep('forgot', 2),
  });
  const {
    busy,
    error,
    badField,
    setBadField,
    sentTo,
    resetCooldown,
    confirmCooldown,
    emailRef,
    turnstileRef,
    clearErrors,
  } = requests;

  const schema = useMemo(() => {
    const isLogin = flow === 'login';
    return yup.object({
      name: yup.string().trim().required(copy('validation.nameRequired')),
      email: emailRule(copy),
      password: yup
        .string()
        .required(
          copy(
            isLogin ? 'validation.passwordRequired' : 'validation.passwordNew'
          )
        )
        .min(
          6,
          copy(
            isLogin
              ? 'validation.passwordShortLogin'
              : 'validation.passwordShort'
          )
        ),
      // Typed twice, so a typo can't become the account's password
      passwordConfirm: yup
        .string()
        .required(copy('validation.passwordConfirmRequired'))
        .oneOf([yup.ref('password')], copy('validation.passwordMismatch')),
      terms: yup.boolean().oneOf([true], copy('validation.terms')),
    });
  }, [copy, flow]);

  const {
    register,
    trigger,
    getValues,
    watch,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      email: '',
      password: '',
      passwordConfirm: '',
      terms: false,
    },
  });

  const steps = flow ? FLOW_STEPS[flow] : [];
  // A sent screen with nothing sent (a reload) starts its flow over
  const asked = flow === 'forgot' && !sentTo ? 1 : searchParams.get('step');
  const index = flow ? stepOf(asked, steps, getValues()) : 0;
  const step = steps[index];
  const isLast = flow ? index === steps.length - 1 : false;
  const firstName = (watch('name') || '').trim().split(/\s+/)[0];

  // Another question on screen: the server's last answer no longer applies
  useEffect(() => {
    clearErrors();
  }, [step, clearErrors]);

  const wasExpired = useRef(false);
  useEffect(() => {
    if (!isExpired || wasExpired.current) return;
    wasExpired.current = true;
    trackEvent(
      'modal_login_expired',
      'Modal Login Expired',
      'modal_login',
      true
    );
  }, [isExpired]);

  const open = (nextFlow) => {
    if (nextFlow === 'login')
      trackEvent(
        'app_welcome_login',
        'App Welcome - Click Login',
        'app_welcome',
        true
      );
    if (nextFlow === 'signup')
      trackEvent(
        'app_welcome_signup',
        'App Welcome - Click Signup',
        'app_welcome',
        true
      );
    goStep(nextFlow, 1);
  };

  const goBack = () => {
    if (busy) return;
    if (locationKey !== 'default' && !(isLoginRoute && index === 0))
      navigate(-1);
    else navigate('/', { replace: true });
  };

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    const isValid = await trigger(STEP_FIELDS[step]);
    if (!isValid) return;
    if (!isLast) {
      if (STEP_FIELDS[step].includes('email'))
        emailRef.current = getValues('email').trim();
      goStep(flow, index + 2);
      return;
    }
    const values = getValues();
    if (flow === 'login')
      requests.handleLogin({
        identifier: values.email.trim(),
        password: values.password,
      });
    else requests.handleSignup(values);
  };

  if (!flow) {
    return (
      <div className="mt-root mt-aw">
        <img className="mt-aw__logo" src={LOGO} alt="" />
        <div className="mt-aw__door">
          <h1 className="mt-aw__name">{t('appName')}</h1>
          <p className="mt-aw__slogan">{own('slogan')}</p>
        </div>
        <div className="mt-aw__doors">
          <button
            type="button"
            className="mt-btn mt-btn--primary"
            onClick={() => open('signup')}
          >
            {copy('tabSignup')}
          </button>
          <button
            type="button"
            className="mt-btn mt-aw__secondary"
            onClick={() => open('login')}
          >
            {copy('tabLogin')}
          </button>
        </div>
      </div>
    );
  }

  const toForgot = () => goStep('forgot', 1);
  const toLogin = () => goStep('login', getValues('email') ? 2 : 1);
  const alert = error && (
    <ServerAlert
      kind={error.kind}
      action={error.action}
      copy={copy}
      email={emailRef.current}
      onForgot={toForgot}
      onLogin={toLogin}
      resend={{
        onClick: () => requests.resendConfirmation(flow),
        cooldown: confirmCooldown,
      }}
    />
  );
  const turnstile = (
    <TurnstileField ref={turnstileRef} action={flow} theme={theme} />
  );
  const name = register('name');
  const email = register('email', {
    onChange: (event) => {
      emailRef.current = event.target.value;
    },
  });
  const password = register('password', {
    onChange: () =>
      setBadField((current) => (current === 'password' ? null : current)),
  });

  const emailField = (hint, autoComplete) => (
    <Field
      id="mt-aw-email"
      label={copy('fields.email')}
      error={errors.email?.message}
      hint={hint}
    >
      {(describedBy) => (
        <input
          {...email}
          id="mt-aw-email"
          className="mt-ad-input"
          type="email"
          inputMode="email"
          autoComplete={autoComplete}
          autoCapitalize="none"
          spellCheck={false}
          autoFocus
          aria-invalid={!!errors.email || undefined}
          aria-describedby={describedBy}
        />
      )}
    </Field>
  );

  // The address the password goes with, for the phone's saved passwords: its own question was a screen ago
  const savedEmail = (
    <input
      type="email"
      name="username"
      autoComplete="username"
      value={getValues('email')}
      readOnly
      hidden
    />
  );

  const fields = {
    loginEmail: emailField(undefined, 'username'),
    loginPassword: (
      <>
        {savedEmail}
        <Field
          id="mt-aw-password"
          label={copy('fields.password')}
          error={errors.password?.message}
        >
          {(describedBy) => (
            <PasswordInput
              id="mt-aw-password"
              registration={password}
              autoComplete="current-password"
              invalid={!!errors.password || badField === 'password'}
              describedBy={describedBy}
              busy={!!busy}
              t={t}
              autoFocus
            />
          )}
        </Field>
      </>
    ),
    signupName: (
      <Field
        id="mt-aw-name"
        label={copy('fields.name')}
        error={errors.name?.message}
      >
        {(describedBy) => (
          <input
            {...name}
            id="mt-aw-name"
            className="mt-ad-input"
            autoComplete="name"
            autoFocus
            aria-invalid={!!errors.name || undefined}
            aria-describedby={describedBy}
          />
        )}
      </Field>
    ),
    signupEmail: emailField(copy('signup.emailHint'), 'username'),
    signupPassword: (
      <>
        {savedEmail}
        <Field
          id="mt-aw-password"
          label={copy('fields.password')}
          error={errors.password?.message}
          hint={copy('signup.passwordHint')}
        >
          {(describedBy) => (
            <PasswordInput
              id="mt-aw-password"
              registration={password}
              autoComplete="new-password"
              invalid={!!errors.password}
              describedBy={describedBy}
              busy={!!busy}
              t={t}
              autoFocus
            />
          )}
        </Field>
        <Field
          id="mt-aw-password-confirm"
          label={copy('fields.passwordConfirm')}
          error={errors.passwordConfirm?.message}
        >
          {(describedBy) => (
            <PasswordInput
              id="mt-aw-password-confirm"
              registration={register('passwordConfirm')}
              autoComplete="new-password"
              invalid={!!errors.passwordConfirm}
              describedBy={describedBy}
              busy={!!busy}
              t={t}
            />
          )}
        </Field>
      </>
    ),
    signupTerms: (
      <div className="mt-ad-field">
        <label
          className={`mt-ad-terms${errors.terms ? ' mt-ad-terms--invalid' : ''}`}
        >
          <input
            {...register('terms')}
            type="checkbox"
            disabled={!!busy}
            aria-invalid={!!errors.terms || undefined}
            aria-describedby={errors.terms ? 'mt-aw-terms-desc' : undefined}
          />
          <span>
            {t('standalone.agreeTerms')}{' '}
            <a href={t('termsUrl')} target="_blank" rel="noopener noreferrer">
              {t('standalone.agreeTermsLinkText')}
            </a>{' '}
            {t('standalone.agreePrivacy')}{' '}
            <a href={t('privacyUrl')} target="_blank" rel="noopener noreferrer">
              {t('standalone.privacyLinkText')}
            </a>
          </span>
        </label>
        {errors.terms && (
          <span className="mt-ad-error" id="mt-aw-terms-desc">
            <CircleAlert aria-hidden="true" />
            {errors.terms.message}
          </span>
        )}
      </div>
    ),
  };

  const direction = navigationType === 'POP' ? 'is-back' : 'is-forward';
  const hasQuestion = !!STEP_FIELDS[step];
  const question =
    step === 'signupTerms'
      ? own(firstName ? 'questions.signupTerms' : 'questions.signupTermsBare', {
          name: firstName,
        })
      : own(`questions.${step}`);

  return (
    <div
      className="mt-root mt-ad mt-aw mt-aw--steps"
      style={{ '--mt-aw-keyboard': `${keyboard.inset}px` }}
    >
      <img className="mt-aw__logo" src={LOGO} alt={t('appName')} />
      <div className="mt-aw__top">
        <button
          type="button"
          className="mt-round"
          onClick={goBack}
          disabled={!!busy}
          aria-label={t('standalone.goBack')}
        >
          <ChevronLeft aria-hidden="true" />
        </button>
      </div>
      {hasQuestion && (
        <div className="mt-aw__bar" aria-hidden="true">
          {steps.map((name_, position) => (
            <i
              key={name_}
              className={position <= index ? 'is-on' : undefined}
            />
          ))}
        </div>
      )}

      <div className="mt-aw__stage">
        <div key={`${flow}-${step}`} className={`mt-aw__step ${direction}`}>
          {hasQuestion ? (
            <form className="mt-ad-form" noValidate onSubmit={submit}>
              <h1 className="mt-aw__question">{question}</h1>
              {isExpired && step === 'loginEmail' && (
                <div className="mt-ad-alert mt-ad-alert--notice" role="status">
                  <Clock aria-hidden="true" />
                  <p>{copy('login.expired')}</p>
                </div>
              )}
              {fields[step]}
              {isLast && turnstile}
              {alert}
              <SubmitButton
                busy={!!busy}
                label={
                  !isLast
                    ? own('continue')
                    : copy(flow === 'login' ? 'login.submit' : 'signup.submit')
                }
                busyLabel={copy(
                  busy === 'verify'
                    ? 'busy.verify'
                    : flow === 'login'
                      ? 'busy.login'
                      : 'busy.signup'
                )}
              />
              {step === 'loginPassword' && (
                <button
                  type="button"
                  className="mt-ad-link mt-aw__forgot"
                  onClick={toForgot}
                  disabled={!!busy}
                >
                  {copy('login.forgot')}
                </button>
              )}
            </form>
          ) : (
            <>
              <h1 className="mt-aw__question">
                {copy(
                  step === 'forgotEmail'
                    ? 'forgot.title'
                    : step === 'forgotSent'
                      ? 'forgotSent.title'
                      : 'signupSent.title'
                )}
              </h1>
              {step === 'forgotEmail' && (
                <>
                  <p className="mt-aw__sub">{copy('forgot.sub')}</p>
                  <ForgotForm
                    copy={copy}
                    busy={busy}
                    defaultEmail={emailRef.current}
                    autoFocus
                    cooldown={resetCooldown}
                    alert={alert}
                    onSubmit={({ email: address }) =>
                      requests.requestReset(address.trim())
                    }
                    onEmailChange={(value) => {
                      emailRef.current = value;
                    }}
                    onLogin={toLogin}
                  />
                </>
              )}
              {step === 'forgotSent' && (
                <ForgotSent
                  copy={copy}
                  busy={busy}
                  email={sentTo}
                  cooldown={resetCooldown}
                  alert={alert}
                  onLogin={toLogin}
                  onResend={() => requests.requestReset(sentTo)}
                  onChangeEmail={() => goStep('forgot', 1)}
                />
              )}
              {step === 'signupSent' && (
                <SignupSent
                  copy={copy}
                  email={sentTo}
                  cooldown={confirmCooldown}
                  alert={alert}
                  onLogin={toLogin}
                  onResend={() => requests.resendConfirmation('signupSent')}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
